import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Text,
  IconButton,
  Spacer,
  HStack,
  VStack,
  Button,
  Flex,
} from '@chakra-ui/react';
import {
  ArrowDownOutline,
  Badge,
  CloseCircleOutline,
  formatCurrency,
  Individual,
  individualFullName,
  formatNumber,
  TickCircleOutline,
} from '@aslanpay/portal-common-lib';

import { ReactNode } from 'react';

import { differenceInCalendarDays } from 'date-fns';
import getPointsValue from 'app/utils/points';

import { RecognitionDetailsModalProps } from './RecognitionDetailsModal.types';

function FieldContainer({ title, content }: { title: string; content: ReactNode }) {
  return (
    <VStack spacing="5px" align="stretch" p="10px" borderRadius="5px" bg="brand.lightPurple">
      <Text textStyle="smallSemiBold" color="brand.darkPurple">
        {title}
      </Text>
      {typeof content === 'string' ? (
        <Text textStyle="smallMedium" color="brand.mutedPurple">
          {content}
        </Text>
      ) : (
        content
      )}
    </VStack>
  );
}

function RecognitionDetailsModal({
  isOpen,
  isInsufficientBalance = false,
  onClose,
  onApprove,
  onReject,
  recognition,
}: RecognitionDetailsModalProps) {
  if (!recognition) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent sx={{ maxWidth: '592px !important' }}>
        <ModalHeader pb={4}>
          <HStack align="flex-start">
            <Flex direction="row" align="center" gap={2}>
              <Text as="h4" textStyle="h4Bold" color="brand.black">
                Recognition Details
              </Text>
            </Flex>

            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close modal"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody pb={6}>
          <VStack spacing="10px" align="stretch">
            <VStack
              spacing="10px"
              align="stretch"
              p="20px 20px 15px 20px"
              borderRadius="10px"
              bg="brand.white"
              border="1px solid"
              borderColor="brand.lightPurple"
              shadow="lg"
            >
              <Flex direction="column" gap="4px">
                <Text textStyle="smallSemiBold" color="brand.purple">
                  {individualFullName({
                    firstName: recognition.senderFirstName,
                    lastName: recognition.senderLastName,
                  } as Individual)}
                  {recognition.senderJobTitle && (
                    <Text as="span" textStyle="smallRegular" color="brand.darkGray">
                      {' '}
                      {`(${recognition.senderJobTitle}) `}
                    </Text>
                  )}
                </Text>

                <ArrowDownOutline boxSize="24px" color="brand.mediumPurple" />

                <Text textStyle="smallSemiBold" color="brand.purple">
                  {individualFullName({
                    firstName: recognition.receiverFirstName,
                    lastName: recognition.receiverLastName,
                  } as Individual)}
                  {recognition.receiverJobTitle && (
                    <Text as="span" textStyle="smallRegular" color="brand.darkGray">
                      {' '}
                      {`(${recognition.receiverJobTitle}) `}
                    </Text>
                  )}
                </Text>
              </Flex>

              {recognition.points != null && (
                <Text as="h4" textStyle="h4Bold" color="brand.darkPurple">
                  {formatNumber(recognition.points)} points{' '}
                  <Text as="span" textStyle="smallRegular" color="brand.darkGray">
                    {`(worth ${formatCurrency(getPointsValue(recognition.points))})`}
                  </Text>
                </Text>
              )}
            </VStack>

            {recognition.sourcePotName && (
              <FieldContainer title="Pot" content={recognition.sourcePotName} />
            )}

            {isInsufficientBalance && (
              <Flex>
                <Badge status="left" size="md" value="Insufficient balance" />
              </Flex>
            )}

            {recognition.values && recognition.values.length > 0 && (
              <FieldContainer
                title="Values"
                content={recognition.values.map((value) => value.valueName).join(', ')}
              />
            )}

            <FieldContainer
              title="Their message to the recipient"
              content={recognition.message || 'No message provided.'}
            />

            <Text as="p" textStyle="smallMedium" color="brand.darkGray">
              {`${differenceInCalendarDays(
                new Date(),
                new Date(recognition.submittedAt)
              )} day(s) ago`}
            </Text>

            <HStack spacing={3} justify="flex-end">
              <Button
                variant="danger"
                leftIcon={<CloseCircleOutline boxSize="20px" />}
                onClick={onReject}
              >
                Decline
              </Button>
              <Button
                leftIcon={<TickCircleOutline boxSize="20px" />}
                onClick={onApprove}
                isDisabled={isInsufficientBalance}
                title={isInsufficientBalance ? 'Source pot has insufficient balance' : undefined}
              >
                Approve
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default RecognitionDetailsModal;
